// agendaApp.ts
import * as Handlebars from 'handlebars';
import { inlineContent } from "juice";

// --- Interfaces (Define based on expected data structure from API and Agenda.html) ---
export interface relations {
    rel: string; // Relation type (e.g., "Hyperlink", "ArtifactLink")
    url: string; // URL to the related item
    attributes?: { [key: string]: any };
}

export interface WorkItem {
    id: number;
    fields: {
        'System.Id': number;
        'System.WorkItemType': string;
        'System.Title': string;
        'System.TeamProject': string;
        'SWBC.Department'?: string; // Example custom field
        'SWBC.Application'?: string;
        'SWBC.Risk'?: string;
        'Microsoft.VSTS.Common.DD'?: string; // Deployment Date
        'Microsoft.VSTS.Common.ImpDate'?: string; // Implementation Date
        'SWBC.RelatedTasks'?: string;
        'SWBC.DevMan'?: { 
          displayName: string;
          uniqueName?: string; // Email
        };
        'SWBC.Developer'?: { 
          displayName: string;
          uniqueName?: string; // Email
        };
        'SWBC.Tester'?: { 
          displayName: string;
          uniqueName?: string; // Email
        };
        'SWBC.AutoStart'?: string; // DevOps Attendance
        'SWBC.AS400Task'?: string;
        'SWBC.ApplicationType'?: string;
        'SWBC.AS400.DeployTime'?: string;
        'Custom.YAMLReleasePipelineLink'?: string; // For YAML releases
        'Custom.YAMLReleaseName'?: string;
        'System.State'?: string;
        "System.AssignedTo"?: {
          displayName: string;
          uniqueName?: string; // Email
      };
      "Microsoft.VSTS.Common.Activity"?: string; // For Tasks
      // Add other fields if needed
  };
  relations?: AdoWorkItemRelation[];
  _links: {
      html?: { href: string }; // Web URL
      // Add other links if needed
  };
  url: string;
}



export interface ReleaseItem { // Structure matching the properties used in Agenda.html {{#each Releases}} block
    Department: string; // Department name
    Type: string; // 'R', 'PBI', 'US', 'B' etc.
    WID: number;
    DD: Date;
    WorkItemUrl?: string; // Link to ADO item
    ReleaseName: string;
    ReleaseID?: string,
    Release?: string | null; // Link to ADO release/build
    Application: string;
    TeamProject: string;
    Description: string;
    DeploymentType: string; // Example: 'N/A' if not applicable
    Risk: string;
    DDHumanFormat: string; // Formatted Date/Time string
    RelatedTasks: string;
    DevMan: string;
    DevManEmail: string;
    Developer?: string;
    Tester: string;
    RMAttendance: string; // DevOps Attendance required?
    BuildId?: number; // For classic releases
    PipelineType?: 'build' | 'release';
    Organization: string,
    lpo?: boolean; // Flags for different orgs/projects if needed
    suretrack?: boolean;
    lendone?: boolean;
}

export interface DepartmentData {
    DepartmentName: string;
    Releases: ReleaseItem[];
}

export interface AgendaData { // Structure matching top-level object in Agenda.html
    Subject: string;
    OnCall: string;
    OnCallNextWeek: string;
    DepartmentReleaseSet: DepartmentData[];
    Signature: string; // HTML signature content
    stylesDoNotInline: string; // Placeholder for CSS styles if needed
}

export interface TechNotifyData { // Structure matching top-level object in TechNotify.html
    Subject: string;
    Releases: ReleaseItem[]; // Array of release items for Tech Notify
    OnCall: string; // Who is sending the notification
    Signature: string; // HTML signature content
    stylesDoNotInline: string; // Placeholder for CSS styles if needed
}

// interface CoordData { // Structure matching top-level object in TechNotify.html
//     Subject: string;
//     Releases: ReleaseItem[]; // Array of release items for Tech Notify
//     OnCall: string; // Who is sending the notification
//     Signature: string; // HTML signature content
//     stylesDoNotInline: string; // Placeholder for CSS styles if needed
// }

// --- Interfaces for ADO API Response (subset) ---
export interface AdoClassicReleaseRef {
    id: number;
    name: string;
    url: string; // API URL for the release details
    _links?: { // Usually present, contains web URL
        web?: {
            href: string;
        };
    };
    // Add other potentially useful fields like status, createdOn if needed
}

export interface AdoClassicReleaseListResponse {
    count: number;
    value: AdoClassicReleaseRef[];
}

export interface FoundReleaseInfo {
    releaseId: number;
    releaseName: string;
    releaseUrl: string; // This should be the *web* URL for user linking
}

export interface AdoConfig {
    orgUrl: string;
    lpoOrgUrl: string;
    suretrackOrgUrl: string;
    pat: string; // This should be the *web* URL for user linking
}

// --- Interfaces for ADO API Responses (Simplified) ---
interface AdoWorkItemRef {
  id: number;
  url: string; // API URL
}

interface AdoWorkItemRelation {
  rel: string; // e.g., "System.LinkTypes.Hierarchy-Forward"
  url: string; // API URL of the related item
  attributes?: { [key: string]: any };
}

interface AdoWorkItem {
  id: number;
  rev: number;
  fields: {
      "System.Id": number;
      "System.WorkItemType": string;
      "System.Title": string;
      "System.State": string;
      "System.Tags"?: string;
      "System.AssignedTo"?: {
          displayName: string;
          uniqueName?: string; // Email
      };
      "Microsoft.VSTS.Common.Activity"?: string; // For Tasks
      // Add other fields if needed
  };
  relations?: AdoWorkItemRelation[];
  _links: {
      html?: { href: string }; // Web URL
      // Add other links if needed
  };
  url: string; // API URL
}

interface AdoBuild {
  id: number;
  // Add other build fields if needed
}

interface AdoReleaseDefinition {
  id: number;
  name: string;
}

interface AdoReleaseEnvironment {
  id: number;
  name: string;
  status: string; // e.g., 'succeeded', 'rejected', 'inProgress'
}
interface AdoGitRepo {
  id: string;
  name: string;
}

interface AdoReleaseArtifact {
  type: string; // e.g., 'Build', 'Git'
  definitionReference: {
      repository?: { id: string; name: string }; // For Git artifacts
      // Add other artifact type definitions if needed
  };
  isPrimary: boolean;
}

interface AdoRelease {
  id: number;
  name: string;
  releaseDefinition: AdoReleaseDefinition;
  environments: AdoReleaseEnvironment[];
  artifacts: AdoReleaseArtifact[];
  // Add other release fields if needed
}

interface AdoCommitRef {
  commitId: string;
  author?: { name: string; email: string; date: string };
  comment?: string;
  url?: string; // API URL for commit details
  workItems?: AdoWorkItemRef[]; // Linked work items (from specific API call)
}

interface AdoCommitChanges {
  changeCounts: { [key: string]: number };
  changes: {
      item: {
          gitObjectType: string;
          path: string;
          url: string; // API URL for item
      };
      changeType: string; // e.g., 'edit', 'add'
  }[];
}

interface ReleaseDetails {
  organization: string;
  project: string;
  pipelineType: 'build' | 'release';
  id: string;
}

interface RWISViolatorWorkItem {
  DeployDate: string;
  DevMan: string;
  DevManEmail: string;
  Developer: string;
  ReleaseURL: string;
  ReleaseID: string;
  ReleaseName: string;
  Description: string;
}

// --- Configuration (Replace with your actual details) ---
// const ADO_CONFIG = {
//     orgUrl: "https://dev.azure.com//SWBC-FigWebDev", // e.g., https://dev.azure.com/SWBC-FigWebDev
//     // Separate configs might be needed for FigLpo, FigSure-Track if they use different PATs/Orgs
//     lpoOrgUrl: "https://dev.azure.com/SWBC-FigLpo",
//     suretrackOrgUrl: "https://dev.azure.com/SWBC-FIGSure-Track",
//     // !! IMPORTANT: Handle PAT securely. DO NOT hardcode here in production client-side code. !!
//     // Consider a backend proxy or secure authentication flow.
//     pat: "EXGnTzrv7KKuCQH3VeXaeLRMo0q2XDTxTttkTIHHDRVWsYMkmVCxJQQJ99BCACAAAAAScRVNAAAGAZDO1EVu",
//     lpoPat: "EXGnTzrv7KKuCQH3VeXaeLRMo0q2XDTxTttkTIHHDRVWsYMkmVCxJQQJ99BCACAAAAAScRVNAAAGAZDO1EVu",
//     suretrackPat: "EXGnTzrv7KKuCQH3VeXaeLRMo0q2XDTxTttkTIHHDRVWsYMkmVCxJQQJ99BCACAAAAAScRVNAAAGAZDO1EVu",
// };

// --- DOM Elements ---
// const dateFromInput = document.getElementById('dateFrom') as HTMLInputElement; // this is a date input field
// const dateToInput = document.getElementById('dateTo') as HTMLInputElement; // this is a date input field

// --- Declare global variables ---
const rotation = [
    "Ryan Raef (303) 525-5879 <a href='https://teams.microsoft.com/l/chat/0/0?users=ryan.raef@swbc.com'>Teams</a>",
    "Roland Valdez (210) 381-4793 <a href='https://teams.microsoft.com/l/chat/0/0?users=roland.valdez@swbc.com'>Teams</a>",
    "Anvesh Muppeda (806) 401-3086 <a href='https://teams.microsoft.com/l/chat/0/0?users=anvesh.muppeda@swbc.com'>Teams</a>",
    "Suresh Patibandla (210) 319-0919 <a href='https://teams.microsoft.com/l/chat/0/0?users=spatibandla@swbc.com'>Teams</a>",
    "Ted McCormick (210) 823-6753 <a href='https://teams.microsoft.com/l/chat/0/0?users=edward.mccormick@swbc.com'>Teams</a>",
    "Manasa Kota (210) 501-6026 <a href='https://teams.microsoft.com/l/chat/0/0?users=saimanasa.kota@swbc.com'>Teams</a>"
]
// const agendaTemplatePath = '../Assets/Agenda.html';
// const techNotifyTemplatePath = '../Assets/TechNotify.html';
// const coordTemplatePath = '../Assets/TechNotify.html';
// const signaturePath = '../Assets/Signature.html';
// const cssDoNotInlinePath = '../Assets/styles-doNotInline.css';
// const stylesPath = '../Assets/styles.css';
// const agendaTemplateRaw = `##Agenda##`
// const techTemplateRaw = `##TechNotify##`
// const coordTemplateRaw = `##Coord##`
// const signature = `##Signature##`
// const stylesDoNotInline = `##styles-doNotInline`
// const stylesCSS = `##styles##`

// TODO Allow onCallOverride as arguments in whatever the execution function ends up being
// const onCallOverride = document.getElementById('On-Call')
// const onCallNextWeekOverride = document.getElementById('On-CallNextWeek')

// --- State ---
let compiledAgendaTemplate: Handlebars.TemplateDelegate<AgendaData> | null = null; // Compiled template
let compiledTechNotifyTemplate: Handlebars.TemplateDelegate<TechNotifyData> | null = null; // Compiled template for Tech Notify
let compiledCoordTemplate: Handlebars.TemplateDelegate<TechNotifyData> | null = null; // Compiled template for Coordination Email

// --- API Functions ---

// Helper to load config.json
// function getConfigJson(pathway: string ?? null): AdoConfig {
async function getConfigJson(): Promise<AdoConfig> {

    try {
        const configContent = await loadFile('../config.json');
        console.log("From the SRA NPM Package: config.json loaded successfully.")
        console.log(configContent)
        return JSON.parse(configContent);
    }
    catch (error) {
        console.error('Error reading config.json:', error);
        throw "NPM SRA PackageUnable to find ADO_CONFIG, will be unable to auth!"
    }
}

async function loadFile(filePath: string): Promise<string> {
    if (typeof window !== 'undefined') {
        // Browser context: Use fetch to load the file
        const response = await fetch(filePath);
        if (!response.ok) {
            throw new Error(`Failed to load file: ${filePath}`);
        }
        return await response.text();
    } else {
        // TODO: handle when we're running in the context of a Lambda
    }
}

// async function readTemplate(templatePath:string): Promise<string> {
//     try {
//         const template = loadFile(templatePath);;
//         console.log(`Read file from ${templatePath}`);
//         return template
//     } catch (error) {
//         console.error('Error reading file from ',templatePath,':', error)
//         return `Error reading file from ${templatePath}: ${error}`;
//     }
// } 

// Helper to create Auth Headers
function getAuthHeader(token: string): HeadersInit {
    return {
        'Authorization': `Basic ${btoa(`:${token}`)}`,
        'Content-Type': 'application/json'
    };
}

// Generic function to query ADO Work Items using WIQL
async function queryWorkItems(orgUrl: string, pat: string, wiql: string): Promise<number[]> {
    const url = `${orgUrl}/_apis/wit/wiql?api-version=7.0`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeader(pat),
            body: JSON.stringify({ query: wiql })
        });
        if (!response.ok) {
            throw new Error(`API Error (${response.status}): ${await response.text()}`);
        }
        const data = await response.json();
        return data.workItems?.map((item: { id: number }) => item.id) ?? [];
    } catch (error) {
        console.error("Error querying work items:", error);
        throw error; // Re-throw to be caught by caller
    }
}

// Generic function to get full details for a batch of work items
async function getWorkItemBatch(orgUrl: string, pat: string, ids: number[]): Promise<WorkItem[]> {
    if (!ids || ids.length === 0) return [];
    const url = `${orgUrl}/_apis/wit/workitemsbatch?$expand=all&api-version=6.0`;
    // Define specific fields if needed to optimize, matching the PS script
    // const fields = [
    //     "System.Id", "System.WorkItemType", "System.Title", "System.TeamProject", "System.State",
    //     "System.AssignedTo", "System.AreaPath", "System.Tags",
    //     "Microsoft.VSTS.Common.Risk", "Microsoft.VSTS.Common.DD", "Microsoft.VSTS.Common.ImpDate",
    //     "SWBC.Department", "SWBC.Application", "SWBC.DeploymentType", "SWBC.RelatedTasks",
    //     "SWBC.DevMan", "SWBC.Developer", "SWBC.Tester", "SWBC.AutoStart", "SWBC.AS400Task",
    //     "SWBC.ApplicationType", "SWBC.AS400.DeployTime",
    //     "Custom.YAMLReleasePipelineLink", "Custom.YAMLReleaseName"
    //     // Add any other required fields
    // ];
    // const $expand = "all"; // Expand all fields, or specify as needed

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeader(pat),
            body: JSON.stringify({ ids: ids, $expand: 'all' }) // Uncomment fields if needed
        });
        if (!response.ok) {
            throw new Error(`API Error (${response.status}): ${await response.text()}`);
        }
        const data = await response.json();
        console.log(data.value); // Debugging output
        return data.value ?? [];
    } catch (error) {
        console.error("Error getting work item batch:", error);
        throw error;
    }
}

async function getWorkItemBatchRWIS(orgUrl: string, pat: string, ids: number[]): Promise<WorkItem[]> {
  if (!ids || ids.length === 0) return [];
  const url = `${orgUrl}/_apis/wit/workitemsbatch?$expand=all&api-version=6.0`;
  // Define specific fields if needed to optimize, matching the PS script
  const fields = [
      "System.Id", "System.WorkItemType", "System.Title", "System.TeamProject", "System.State",
      "System.AssignedTo", "System.AreaPath", "System.Tags",
      "Microsoft.VSTS.Common.Risk", "Microsoft.VSTS.Common.DD", "Microsoft.VSTS.Common.ImpDate",
      "SWBC.Department", "SWBC.Application", "SWBC.DeploymentType", "SWBC.RelatedTasks",
      "SWBC.DevMan", "SWBC.Developer", "SWBC.Tester", "SWBC.AutoStart", "SWBC.AS400Task",
      "SWBC.ApplicationType", "SWBC.AS400.DeployTime",
      "Custom.YAMLReleasePipelineLink", "Custom.YAMLReleaseName"
      // Add any other required fields
  ];
  // const $expand = "all"; // Expand all fields, or specify as needed

  try {
      const response = await fetch(url, {
          method: 'POST',
          headers: getAuthHeader(pat),
          body: JSON.stringify({ ids: ids, 'fields': fields }) // Uncomment fields if needed
      });
      if (!response.ok) {
          throw new Error(`API Error (${response.status}): ${await response.text()}`);
      }
      const data = await response.json();
      console.log(data.value); // Debugging output
      return data.value ?? [];
  } catch (error) {
      console.error("Error getting work item batch:", error);
      throw error;
  }
}

async function processApiResponse(
    workItems: WorkItem[],
    itemType: 'DM' | 'Doc' | 'Release' | 'AS400',
    ADO_CONFIG: AdoConfig,
    isLpo = false,
    isSuretrack = false
): Promise<ReleaseItem[]> {
    console.log(`Processing ${workItems.length} items for ${itemType}`);

    const releaseItems = await Promise.all(
        workItems.map(async (item) => {
            const fields = item.fields;
            let deploymentDate: Date | null = null;
            let dateString = fields['Microsoft.VSTS.Common.ImpDate'] ?? fields['Microsoft.VSTS.Common.DD'];

            if (dateString) {
                try {
                    deploymentDate = new Date(dateString);
                    if (itemType === 'AS400' && fields['SWBC.AS400.DeployTime']) {
                        
                        const timeStr = fields['SWBC.AS400.DeployTime'];
                        if (timeStr.includes("After 5 pm")) {
                            deploymentDate.setHours(17);
                            deploymentDate.setMinutes(1);
                        } else if (timeStr.includes("After Nightly")) {
                            deploymentDate.setHours(23)
                            deploymentDate.setMinutes(58);
                        }
                    // console.log(`I got a deploy time of ${deploymentDate}`)
                    }
                } catch (e) {
                    console.error("Date parse error for", dateString, e);
                }
            }

            let releaseName = fields['Custom.YAMLReleaseName'] ?? "TBD";
            let releaseUrl = fields['Custom.YAMLReleasePipelineLink'] ?? null;
            let department = fields['SWBC.Department'] ?? "Unassigned";
            let DevMan = fields['SWBC.DevMan']?.displayName ?? 'N/A';
            let Developer: string
            // if (fields['SWBC.Developer' !== null]) {Developer = fields['SWBC.Developer']?.displayName}
            // else if (fields['System.AssignedTo'].displayName) {Developer = fields['System.AssignedTo'].displayName} 
            // else {Developer = 'Unassigned'};
            let tester = fields['SWBC.Tester']?.displayName ?? "Unassigned"
            let attendance = fields['SWBC.AutoStart'] ?? 'N/A'
            let DevManEmail: string
            if (department === "Enterprise Content Management") department = "Enterprise Platforms and Solutions";
            if (department === "AS400 Development") department = "POWER9 Development";
            if (fields['System.TeamProject'] ===  "Documentum") {department = "Documentum";releaseName = 'N/A'}
            else if (fields['System.TeamProject'] === "AS400" && (( fields['SWBC.AS400Task']?.toLowerCase() == 'none'))) {department = "Data Management"; releaseName = 'N/A'}
            else if (fields['System.TeamProject'] === "AS400" && fields['SWBC.AS400Task'] !== 'none') {department = "POWER9 Development"; DevMan = "Lilly Carlyle";  tester = "N/A"; attendance = "Attended"; DevManEmail = "lcarlyle@swbc.com"}
            else if (fields['System.TeamProject'] === "Data_Management") {department = "Data Management"; releaseName = "N/A"}
            else if (fields['System.TeamProject'] === "AS400-Accounting") {department = "POWER9 Accounting"; DevMan = "Dr. Keith Weekley"; tester = "N/A"; attendance = "Attended"; DevManEmail = "kweekley@swbc.com"}
            else if (fields['System.TeamProject'] === "AS400-Accounting" && fields['SWBC.RelatedTasks'] != null) {department = "POWER9 Development"; DevMan = "Lilly Carlyle";  tester = "N/A"; attendance = "Attended"; DevManEmail = "lcarlyle@swbc.com"}
            else if (fields['System.TeamProject'] === "Payments") {department = "Data Management";}
            else if (fields['System.TeamProject'] === "Payments Redux") {department = "Swivel Engineering";}
            else if (fields['System.TeamProject'] === "Payments") {department = "Swivel Engineering";}
            let buildId: number;
            let pipelineType: 'build' | 'release';
            let organization: string;

            if (department == "POWER9 Accounting" || department == "POWER9 Development") {
                console.log(`In ${item.id} looking for releases/Developer`)
                
                if (fields['System.AssignedTo']) {Developer = fields['System.AssignedTo'].displayName ?? "Unassigned"}
                else if (fields['SWBC.DevMan']) {Developer = fields['SWBC.DevMan'].displayName ?? "Unassigned"}
                else {Developer = 'Unassigned'};
                console.log(`Checking for ${department} release`)
                const targetUrlPattern = "vstfs:///Build/Build/";
                const relations: Array<relations> = item.relations;
                let matchingRelation: string
                if (Array.isArray(relations)) {
                    matchingRelation = relations.find(relation => relation.url.includes(targetUrlPattern))
                }

                if (matchingRelation) {
                    console.log(`Found matching relation URL: ${matchingRelation.url}`);
                    // Extract the number from the URL using a regular expression
                    const match = matchingRelation.url.match(/Build\/Build\/(\d+)/);
                    if (match && match[1]) {
                        buildId = parseInt(match[1], 10); // Convert the extracted string to a number
                    }
                    if (buildId){
                        try {
                            const releaseInfo = await findMatchingClassicReleaseFromBuildId(
                                fields['System.TeamProject'],
                                buildId,
                                ADO_CONFIG
                            );
                            if (releaseInfo) {
                                releaseUrl = releaseInfo.releaseUrl;
                                releaseName = `Release ${releaseInfo.releaseName}`;
                            } else {
                                console.warn(`No matching AS400 release found for build ID "${buildId}"`);
                            }
                        } catch (error) {
                            console.error("Error finding AS400 release:", error);
                        }

                    }
                }
            
            }

            if (!releaseUrl && department !== "Documentum" && department !== "Data Management" && department !== "POWER9 Accounting" && department !== "POWER9 Development") {
                // if (!releaseUrl && department !== "Documentum" && department !== "Data Management") {
                Developer = fields['SWBC.Developer']?.displayName ?? "Unassigned"
                const searchTerm = extractReleaseSearchTermFromTitle(fields['System.Title']);
                const targetUrlPattern = "vstfs:///Build/Build/";
                const relations: Array<relations> = item.relations;
                let matchingRelation: string
                if (relations !== null) {
                    matchingRelation = relations.find(relation => relation.url.includes(targetUrlPattern))
                }
                

                if (matchingRelation) {
                    console.log(`Found matching relation URL: ${matchingRelation.url}`);
                    // Extract the number from the URL using a regular expression
                    const match = matchingRelation.url.match(/Build\/Build\/(\d+)/);
                    if (match && match[1]) {
                        buildId = parseInt(match[1], 10); // Convert the extracted string to a number
                    }
                    if (buildId){
                        try {
                            const releaseInfo = await findMatchingClassicReleaseFromBuildId(
                                fields['System.TeamProject'],
                                buildId,
                                ADO_CONFIG
                            );
                            if (releaseInfo) {
                                releaseUrl = releaseInfo.releaseUrl;
                                releaseName = `Release ${releaseInfo.releaseName}`;
                            } else {
                                console.warn(`No matching AS400 release found for build ID "${buildId}"`);
                            }
                        } catch (error) {
                            console.error("Error finding AS400 release:", error);
                        }

                    }
                }
                else if (searchTerm && !releaseUrl) {
                    releaseName = "TBD";
                    try {
                        const releaseInfo = await findMatchingClassicRelease(
                            ADO_CONFIG.orgUrl,
                            fields['System.TeamProject'],
                            ADO_CONFIG.pat,
                            searchTerm
                        );
                        if (releaseInfo) {
                            releaseUrl = releaseInfo.releaseUrl;
                            releaseName = `Release ${releaseInfo.releaseName}`;
                        } else {
                            console.warn(`No matching classic release found for "${searchTerm}"`);
                        }
                    } catch (error) {
                        console.error("Error finding classic release:", error);
                    }
                } else {
                    console.warn(`Not able to find a related build or parse a search term from the title; manual action probably required.`);
                }

            }
            if ((!buildId || !pipelineType || !organization) && releaseUrl ) {
              const details = await extractDetailsFromUrl(releaseUrl)
              buildId = details?.id
              pipelineType = details?.pipelineType
              organization = details?.organization
              
            }
            let ddHuman: string
            if (itemType === 'AS400' && deploymentDate) {ddHuman = deploymentDate.toLocaleString(
                'en-US', {timeZone: 'America/Chicago', 
                    year: 'numeric', 
                    month: '2-digit', 
                    day: '2-digit'}) + " " + 
                    fields['SWBC.AS400.DeployTime']}
            else if ((itemType === 'DM' || itemType === 'Doc') && deploymentDate) {ddHuman = deploymentDate.toLocaleString(
                'en-US', { timeZone: 'America/Chicago', 
                    year: 'numeric', 
                    month: '2-digit', 
                    day: '2-digit'})}
            else if (deploymentDate) { ddHuman = deploymentDate.toLocaleString('en-US', {       
                year: 'numeric',       
                month: '2-digit',       
                day: '2-digit',       
                hour: '2-digit',       
                minute: '2-digit',       
                hour12: true,})};

            return {
                Department: department,
                Type: mapWorkItemType(fields['System.WorkItemType']),
                WID: item.id,
                DD: new Date(deploymentDate) ?? null,
                WorkItemUrl: `${ADO_CONFIG.orgUrl}/${fields['System.TeamProject']}/_workitems/edit/${fields['System.Id']}`,
                ReleaseName: releaseName,
                Release: releaseUrl,
                Application: fields['SWBC.Application'] ?? fields['SWBC.ApplicationType'] ?? 'N/A',
                TeamProject: fields['System.TeamProject'] ?? 'N/A',
                Description: fields['System.Title'] ?? 'N/A',
                DeploymentType: fields['SWBC.DeploymentType'] ?? 'N/A',
                Risk: fields['Microsoft.VSTS.Common.Risk']?.replace(/^\d - /, "") ?? fields['SWBC.Risk']?.replace(/^\d - /, "") ?? 'N/A',
                DDHumanFormat: ddHuman,
                RelatedTasks: fields['SWBC.RelatedTasks'] ?? fields['SWBC.AS400Task'] ?? 'N/A',
                DevMan: fields['SWBC.DevMan']?.displayName ?? DevMan ?? "Unassigned",
                DevManEmail: DevManEmail ?? fields['SWBC.DevMan']?.uniqueName ?? DevMan ?? "Unknown",
                Developer: Developer ?? 'Unassigned',
                Tester: fields['SWBC.Tester']?.displayName ?? tester ?? 'N/A',
                RMAttendance: attendance,
                BuildId: buildId,
                PipelineType: pipelineType ?? null,
                Organization: organization ?? null,
                lpo: isLpo,
                suretrack: isSuretrack,
            };
        })
    );

    return releaseItems;
}

// Helper to map ADO Work Item Types
function mapWorkItemType(type: string): string {
    switch (type) {
        case "User Story": return "US";
        case "Product Backlog Item": return "PBI";
        case "Bug": return "B";
        case "Release": return "R";
        default: return type;
    }
}

/**
 * Extracts a potential release search term (like "Release 123" or just "123")
 * from a work item title by removing common prefixes/suffixes like sprint info and dates.
 * Based on the regex logic in SendReleaseAgenda.ps1 Convert-Release function.
 *
 * @param workItemTitle The raw title of the work item (e.g., "Sprint 145 - Release 2.1 Deployment - 04/26/2025").
 * @returns The extracted search term (e.g., "Release 2.1", "Release 55", "Some Task 12") or the cleaned title if no specific pattern matches. Returns null if title is empty.
 */
function extractReleaseSearchTermFromTitle(workItemTitle: string): string | null {
    if (!workItemTitle) {
        return null;
    }

    console.debug(`Original title for search term extraction: "${workItemTitle}"`);

    // Regex patterns based on PowerShell script
    const sprintRegex = /[sS][pP][rR][iI][nN][tT]\s*\d*[^a-zA-Z]*/gi; // Matches "Sprint 123", "Sprint-123", "sprint", etc.
    const dateRegex = /(\d{1,4}\/){2}\d{1,4}/g; // Matches MM/DD/YYYY or similar
    const releaseWithDotVersionRegex = /[rR]elease[\s\-‒–—―]+.*?((\d+)\.(\d+)(\.\d+)?)/i; // Matches "Release 1.2", "Release 1.2.3" - captures version number
    const releaseWithNumberRegex = /[rR]elease[\s\-‒–—―]+\D*(\d+)/i; // Matches "Release 123", "Release-45" - captures number
    const simpleKeywordNumberRegex = /(\b[a-zA-Z]+(?:[\s\-‒–—―]+[a-zA-Z]+)*)[\s\-‒–—―]+(\d+(\.\d+)*)\b/i; // Fallback: "Words 123", "Words 1.2" - captures words and number

    // Remove sprints and dates first
    let cleanedTitle = workItemTitle.replace(sprintRegex, "").replace(dateRegex, "").trim();
    // Remove potential leading/trailing non-alphanumeric characters left after replacements
    cleanedTitle = cleanedTitle.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, "").trim();


    console.debug(`Cleaned title: "${cleanedTitle}"`);

    // Try matching specific patterns
    let match = cleanedTitle.match(releaseWithDotVersionRegex);
    if (match) {
        // Return the full matched string like "Release 2.1"
        console.debug(`Matched dot version regex: "${match[0]}"`);
        // We might want just the number part for matching later, but the PS script seems to match on the whole name first. Return the matched name part.
        return match[0].trim();
    }

    match = cleanedTitle.match(releaseWithNumberRegex);
    if (match) {
        // Return the full matched string like "Release 123"
        console.debug(`Matched release number regex: "${match[0]}"`);
        return match[0].trim();
    }

    match = cleanedTitle.match(simpleKeywordNumberRegex);
     // Avoid matching if the keyword part contains 'sprint' case-insensitively
    if (match && !/sprint/i.test(match[1])) {
        console.debug(`Matched keyword number regex: "${match[0]}" (Keyword: "${match[1]}")`);
        // Return the matched part like "Some Task 123"
        return match[0].trim();
    }

    // Fallback: If no specific pattern matched, return the cleaned title
    console.debug(`No specific pattern matched, returning cleaned title: "${cleanedTitle}"`);
    return cleanedTitle || null; // Return null if cleaning resulted in empty string
}

/**
 * Normalizes a release name or search term to primarily its numeric components
 * for comparison purposes, similar to the PowerShell script's comparison logic.
 * Example: "Release 1.2.3" -> "123", "Deploy 45" -> "45"
 *
 * @param name The release name or search term.
 * @returns The normalized string containing only digits.
 */
function normalizeForNumericComparison(name: string | null | undefined): string {
    if (!name) {
        return "";
    }
    // Remove all non-digit characters
    return name.replace(/[^0-9]/g, "");
}

/**
 * Fetches recent classic releases from Azure DevOps and attempts to find one
 * matching the provided search term, primarily by comparing normalized numeric parts.
 * Based on the API call and comparison logic in SendReleaseAgenda.ps1 Convert-Release function.
 *
 * @param orgUrl The base URL of the Azure DevOps organization (e.g., "https://dev.azure.com/MyOrg").
 * @param project The name of the team project.
 * @param pat The Personal Access Token for authentication.
 * @param searchTerm The potential release identifier extracted from the work item title.
 * @returns A promise resolving to the matched release info (ID, name, web URL) or null if no match found.
 */
async function findMatchingClassicRelease(
    orgUrl: string,
    project: string,
    pat: string,
    searchTerm: string | null
): Promise<FoundReleaseInfo | null> {

    if (!searchTerm) {
        console.warn("Cannot find matching release: Search term is empty.");
        return null;
    }

    // Normalize the search term for comparison
    const normalizedSearchTerm = normalizeForNumericComparison(searchTerm);
    if (!normalizedSearchTerm) {
        console.warn(`Cannot find matching release: Normalized search term "${searchTerm}" is empty.`);
        return null;
    }

    console.log(`Searching for classic release in ${orgUrl}/${project} matching term "${searchTerm}" (normalized: "${normalizedSearchTerm}")`);

    let searchUrl: string = orgUrl.replace("https://dev.azure.com/", "https://vsrm.dev.azure.com/"); // uses a different apex URL, but we still need the organization name

    // Construct the API URL
    // Note: vsrm.dev.azure.com is often used for release APIs, but dev.azure.com should also work.
    // Using $top=100 to match the PS script's limit.
    const apiUrl = `${searchUrl}/${encodeURIComponent(project)}/_apis/release/releases?$top=100&api-version=6.0`;

    try {
        // Fetch the list of releases
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: getAuthHeader(pat) // Assumes getAuthHeader function exists
        });

        if (!response.ok) {
            throw new Error(`API Error (${response.status}) fetching releases from ${project}: ${await response.text()}`);
        }

        const releaseData: AdoClassicReleaseListResponse = await response.json();

        if (!releaseData || !releaseData.value || releaseData.count === 0) {
            console.log(`No classic releases found in project ${project}.`);
            return null;
        }

        // Iterate through the releases and compare
        for (const release of releaseData.value) {
            const releaseName = release.name;
            const normalizedReleaseName = normalizeForNumericComparison(releaseName);

            console.debug(`Comparing search term "${normalizedSearchTerm}" with release "${releaseName}" (normalized: "${normalizedReleaseName}")`);

            // Compare the normalized numeric parts
            if (normalizedReleaseName && normalizedReleaseName === normalizedSearchTerm) {
                console.log(`Match found: Release ID ${release.id}, Name "${releaseName}"`);

                // return release.id
                // Construct the user-friendly web URL
                // Prefer the _links.web.href if available, otherwise construct manually.
                let webUrl = release._links?.web?.href;
                console.log(`Web URL from _links: ${webUrl}`); // Debugging output
                if (!webUrl) {
                     webUrl = `${orgUrl}/${encodeURIComponent(project)}/_releaseProgress?_a=release-pipeline-progress&releaseId=${release.id}`;
                     console.warn(`Could not find _links.web.href for release ${release.id}, constructing URL manually.`);
                }


                return {
                    releaseId: release.id,
                    releaseName: normalizedReleaseName, // Return the original name
                    releaseUrl: webUrl
                };
            }
        }

        // No match found after checking all releases
        console.log(`No classic release found matching normalized term "${normalizedSearchTerm}" in the top 100 releases.`);
        return null;

    } catch (error) {
        console.error(`Error finding matching classic release for project ${project}:`, error);
        // Depending on requirements, you might want to return null or re-throw
        return null;
    }
}

async function findMatchingClassicReleaseFromBuildId(
    project: string,
    buildId: number,
    ADO_CONFIG: AdoConfig
): Promise<FoundReleaseInfo | null> {

    const buildUrl: string = `${ADO_CONFIG.orgUrl.replace("https://dev.azure.com/", "https://vsrm.dev.azure.com")}/${project}/_apis/release/releases?$top=100&artifactVersionId=${buildId}&api-version=7.1`; // uses a different apex URL, but we still need the organization name
    try {
        // Fetch the list of releases
        const response = await fetch(buildUrl, {
            method: 'GET',
            headers: getAuthHeader(ADO_CONFIG.pat) // Assumes getAuthHeader function exists
        });
        if (!response.ok) {
            throw new Error(`API Error (${response.status}) fetching releases from ${project}: ${await response.text()}`);
        }

        const releaseData: AdoClassicReleaseListResponse = await response.json();

        if (!releaseData || !releaseData.value || releaseData.count === 0) {
            console.log(`No classic releases for build id ${buildId} in ${project}.`);
            return null;
        }
        else if (releaseData.value.length === 1) {
            const release = releaseData.value[0];
            const releaseName = release.name;
            const normalizedReleaseName = normalizeForNumericComparison(releaseName);
            console.log(`Exactly ONE match found: Release ID ${release.id}, Name "${releaseName}"`);

            // Construct the user-friendly web URL
            let webUrl = release._links?.web?.href;
            if (!webUrl) {
                webUrl = `${ADO_CONFIG.orgUrl}/${encodeURIComponent(project)}/_releaseProgress?_a=release-pipeline-progress&releaseId=${release.id}`;
                console.warn(`Could not find _links.web.href for release ${release.id}, constructing URL manually.`);
            }

            return {
                releaseId: release.id,
                releaseName: normalizedReleaseName, // Return the original name
                releaseUrl: webUrl
            };
        }
        else if (releaseData.value.length > 1) {
            console.warn(`Multiple classic releases found for build id ${buildId} in ${project}.`);
            return null;
        }
        else {
            console.log(`The number of classic releases found for build id ${buildId} in ${project} is ${releaseData.value.length}`);
            console.log(`SRA does not anticipate this case, and it should not be possible and requires further investigation.`);
            return null;
        }
    } catch (error) {
    console.error(`Error finding matching classic release for project ${project}:`, error);
    // Depending on requirements, you might want to return null or re-throw
    return null;
    }
}


// ** CRITICAL STEP ** : Replicate PowerShell GroupSort-ReleaseItem logic
function groupAndSortDataAgenda(allItems: ReleaseItem[]): DepartmentData[] {
    const grouped: { [key: string]: ReleaseItem[] } = {};

    // TODO: Determine the correct field for grouping (Department, Application, TeamProject?) based on PS script
    // The PS script groups Releases and AS400 by 'Department', DM by 'Department', Doc by 'Department'.
    allItems.forEach(item => {
        // Example: Grouping by a 'department' property derived during processing
        const department = item.Department ?? "Unknown Department"; // Assuming 'Department' was added in processApiResponse
        if (!grouped[department]) {
            grouped[department] = [];
        }
        grouped[department].push(item);
    });

    return Object.keys(grouped)
        .map(deptName => ({
            DepartmentName: deptName,
            // Sort releases within each department by date (DDHumanFormat might not sort well, use original date object if possible)
            // TODO: Ensure proper date sorting. Parsing DDHumanFormat back to Date is fragile. Store original Date object.
            Releases: grouped[deptName].sort((a, b) => a.DDHumanFormat.localeCompare(b.DDHumanFormat)) // Placeholder sort
        }))
        // Sort departments alphabetically by name
        .sort((a, b) => a.DepartmentName.localeCompare(b.DepartmentName));
}

function groupAndSortDataTechNotify(
    allItems: ReleaseItem[],
    dateFromInput: string
): ReleaseItem[] {
    console.log(`Grouping and sorting Tech Notify data - received ${allItems.length} items.`);
    // Filter out items from "Data Management" and "Documentum" departments
    const filteredItems = allItems.filter(item => 
        item.Department !== "Data Management" && item.Department !== "Documentum"
    );
    console.log(`Filtered items count for Tech Notify: ${filteredItems.length}`);
    // Get the selected dateFrom value

    const monthFrom = dateFromInput.split("-")[1]
    const dateFrom = dateFromInput.split("-")[2]
    const yearFrom = dateFromInput.split("-")[0]
    console.log(`DateFrom: ${dateFrom}`);
    console.log(`Filtering items with DD matching dateFrom: ${dateFrom}`);

    // Filter items to include only those with a DD matching dateFrom
    const dateFilteredItems = filteredItems.filter(item => 
        item.DD.getDate() === parseInt(dateFrom) && item.DD.getMonth() + 1 === parseInt(monthFrom) && item.DD.getFullYear() === parseInt(yearFrom)
    );

    console.log(`Date filtered items count: ${dateFilteredItems.length}`);

    // Sort the remaining items by the DD field (earliest to latest)
    const sortedItems = dateFilteredItems.sort((a, b) => {
        const dateA = new Date(a.DD);
        const dateB = new Date(b.DD);
        return dateA.getTime() - dateB.getTime();
    });

    console.log(`Sorted items count: ${sortedItems.length}`);

    return sortedItems;
}

function groupAndSortDataCoord(
    allItems: ReleaseItem[],
    dateFromInput: string
): ReleaseItem[] {
    const filteredItems = allItems.filter(item => 
        item.Department == "POWER9 Development" || item.Department == "POWER9 Accounting"
    );
    console.log(`Filtered items for Coord Email count: ${filteredItems.length}`);
    // Get the selected dateFrom value

    const monthFrom = dateFromInput.split("-")[1]
    const dateFrom = dateFromInput.split("-")[2]
    const yearFrom = dateFromInput.split("-")[0]
    console.log(`DateFrom: ${dateFrom}`);
    console.log(`Filtering items with DD matching dateFrom: ${dateFrom}`);

    // Filter items to include only those with a DD matching dateFrom
    const dateFilteredItems = filteredItems.filter(item => 
        item.DD.getDate() === parseInt(dateFrom) && item.DD.getMonth() + 1 === parseInt(monthFrom) && item.DD.getFullYear() === parseInt(yearFrom)
    );

    console.log(`Date filtered items count: ${dateFilteredItems.length}`);

    // Sort the remaining items by the DD field (earliest to latest)
    const sortedItems = dateFilteredItems.sort((a, b) => {
        const dateA = new Date(a.DD);
        const dateB = new Date(b.DD);
        return dateA.getTime() - dateB.getTime();
    });

    console.log(`Sorted items count: ${sortedItems.length}`);

    return sortedItems;
}

// --- Rendering Functions ---

// Fetch and compile the Handlebars template
async function loadTemplate(agendaTemplateString: string, styles: string): Promise<Handlebars.TemplateDelegate<AgendaData>> {
    try {

        const agendaTemplateStringInlined = inlineContent(agendaTemplateString,styles)
        compiledAgendaTemplate = Handlebars.compile(agendaTemplateStringInlined);
        console.log("From the SRA NPM Package: Handlebars template compiled successfully")
        return compiledAgendaTemplate

    } catch (error) {
        console.error("Error loading Handlebars template:", error);
        throw "NPM SRA PackageError loading Handlebars template"
    }
}
async function loadTechTemplate(templateTechNotifyString: string, styles: string): Promise<Handlebars.TemplateDelegate<TechNotifyData>> {
    const templateTechNotifyStringInlined = inlineContent(templateTechNotifyString,styles)
    const compiledTechNotifyTemplate = Handlebars.compile(templateTechNotifyStringInlined);
    console.log("From the SRA NPM Package: Tech Notify template compiled successfully.");
    return compiledTechNotifyTemplate

}

// Render data using the compiled Handlebars template
async function renderAgenda(
    AgendaData: AgendaData | null, 
    compiledAgendaTemplate: Handlebars.TemplateDelegate<AgendaData>
): Promise<string> {

    if (!compiledAgendaTemplate) {
        console.error("Agenda template is not loaded.")
         throw "NPM SRA PackageTemplate is not loaded";
    }

    if (!AgendaData) {
        console.error("No data provided for rendering agenda.");
        throw "NPM SRA PackageNo data provided for rendering agenda."
    }
    
    try {
        console.log("From the SRA NPM Package: Rendering agenda with AgendaData:", AgendaData); // Debugging output
        const agenda = compiledAgendaTemplate(AgendaData);
        // console.log("From the SRA NPM Package: Rendered HTML:", html); // Debugging output
        // agendaOutputDiv.innerHTML = agenda;
        return agenda
    }
    catch (error) {
        console.error("Error rendering agenda Handlebars template:", error);
        throw "NPM SRA PackageError rendering Handlebars template"
    }  
}

async function renderTechNotify(TechNotifyData: TechNotifyData, compiledTechNotifyTemplate: Handlebars.TemplateDelegate<TechNotifyData> ): Promise<string> {
    if (!compiledTechNotifyTemplate) {
        console.error("Tech Notify template is not loaded.")
    }
    try{
        console.log("From the SRA NPM Package: Rendering Tech Notify with TechNotifyData:", TechNotifyData); // Debugging output
        const techNotify = compiledTechNotifyTemplate(TechNotifyData);
        return techNotify
    }
    catch (error) {
        console.error("Error rendering Handlebars template:", error);
        return `Error rendering Handlebars template: ${error}`
    } 

}

function WhoIsOnCall(
    offset: number | null, 
    rotation: Array<string>,
    dateFromInput

) {

    const rotationStart = new Date(2025, 4, 4); // May 4, 2025 (Month is 0-based in JavaScript , and 4th because the rotation 'starts' on Sunday for a zero based weirdness)
    const releaseDateFrom = new Date(dateFromInput); // Use selected date or today
    // console.log(releaseDateFrom)
    const weeksDifference = Math.floor((releaseDateFrom.getTime() - rotationStart.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1 ; // Difference in weeks
    // console.log(weeksDifference)
    let index = (Math.ceil(weeksDifference) + (offset ?? 0)) % rotation.length;
    // console.log(`Looks like the index is ${index}`)
    while(index < 0) {
        if (Math.abs(index) < rotation.length) {index += rotation.length}
        else {index += rotation.length}
    }
    // console.log(`Within WhoIsOnCall function returned ${rotation[index]}`)
    return rotation[index];

}



// --- Main Application Logic ---

export async function loadAndRenderAgenda(
    dateFromInput: string, 
    dateToInput:string | null, 
    onCallOverride: string | null, 
    onCallNextWeekOverride: string | null,
    ADO_CONFIG: AdoConfig,
    agendaTemplateRaw: string,
    techNotifyTemplateRaw: string,
    coordTemplateRaw: string,
    SIGNATURE_HTML: string,
    cssDoNotInline: string,
    styles: string
  ): Promise<Array<string>>{
    
    console.log("From the SRA NPM Package: Hello from the loadAndRenderAgenda function!")
        // This was a lovely experiment but it generates a bunch of CORS failures locally and probably would in an S3 bucket as well :(
    // const agendaTemplateRaw = await readTemplate(agendaTemplatePath)
    const agendaTemplate: Handlebars.TemplateDelegate<AgendaData> = await loadTemplate(agendaTemplateRaw, styles); // Ensure template is loaded before rendering
    // const techNotifyTemplateRaw = await readTemplate(techNotifyTemplatePath)
    const techNotifyTemplate: Handlebars.TemplateDelegate<TechNotifyData>= await loadTechTemplate(techNotifyTemplateRaw, styles)
    // const coordTemplateRaw = await readTemplate(coordTemplatePath)
    const coordTemplate: Handlebars.TemplateDelegate<TechNotifyData> = await loadTechTemplate(coordTemplateRaw, styles)
    // const ADO_CONFIG: AdoConfig = await getConfigJson()



    // agendaOutputDiv.innerHTML = `Loading...this might take a few seconds, depending on the number of releases.<br><br>${getRandomJoke()}`;
    // techNotifyOutputDiv.innerHTML = 'Loading...this might take a few seconds, depending on the number of releases.';
    // coordOutputDiv.innerHTML = `Loading...this might take a few seconds, depending on the number of releases.`;

    const dateFrom = dateFromInput || new Date(new Date().toLocaleDateString('en-US', { timeZone: 'America/Chicago' })).toISOString().split('T')[0]; // Default to today if empty
    const dateTo = dateToInput || dateFrom; // Default to dateFrom if empty
    if (dateFrom > dateTo) {
        console.error("Date From must be less than Date To.")
    }
    const currentTime = new Date().toLocaleTimeString('en-US', { hour12: false }); // Format as HH:mm:ss
    console.log(`Fetching data for date: ${dateFrom} at ${currentTime}`); // Debugging output

    // ** CRITICAL STEP ** : Construct WIQL queries based on PS script and selected date
    // Need to replicate the logic for $OneDate vs $DateRange based on whether ReleaseDateTo is set.
    // This example uses $OneDate logic.
    // const dateFilter = `AND ([Microsoft.VSTS.Common.DD] = '${dateFrom}' OR [Microsoft.VSTS.Common.ImpDate] = '${dateFrom}')`; // Basic date filter
    const dateFilter = `AND (([Microsoft.VSTS.Common.DD] >= '${dateFrom}' AND [Microsoft.VSTS.Common.DD] <= '${dateTo}') OR ([Microsoft.VSTS.Common.ImpDate] >=  '${dateFrom}' AND [Microsoft.VSTS.Common.ImpDate] <= '${dateTo}'))`; // Date range filter
    // const lendOneDateFilter = `AND [Custom.DeploymentDate] = '${dateFrom}'`; // Example for LendOne i.e. new orgs that can't use the same naming convention

    // Construct WIQL queries - **These must match the PS script exactly**
    // Example WIQL (replace {{Dates}} correctly)
    const wiqlDM = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] In ('User Story','Product Backlog Item','Bug') ${dateFilter} AND ([System.AssignedTo] In Group '[Data_Management]\\Big_Efforts' OR [System.AssignedTo] In Group '[Data_Management]\\Implementations') AND ([System.AreaPath] Not Under 'AS400\\Firefighters Kanban' OR [System.AreaPath] Not Under 'AS400\\Client Integration') AND [System.AreaPath] Not Under 'AS400\\Auto Project' AND ([SWBC.AS400Task] = '' OR [SWBC.AS400Task] = 'None' OR [SWBC.AS400Task] = 'none')`;
    const wiqlDoc = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] In ('User Story','Product Backlog Item','Bug') ${dateFilter} AND [System.TeamProject] = 'Documentum' AND [System.State] = 'ReadyToDeploy'`;
    const wiqlReleaseFigWebDev = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType]='Release' ${dateFilter} AND [SWBC.Department]!= 'Swivel Engineering'`; // Default Release Query
    const wiqlReleaseLpo = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType]='Release' ${dateFilter}`; // LPO Release Query
    const wiqlReleaseSuretrack = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType]='Release' ${dateFilter}`; // Suretrack Release Query
    const wiqlAS400 = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] in ('Product Backlog Item', 'Bug') ${dateFilter} AND [SWBC.ApplicationType] in ('Auto', 'Gap', 'Mortgage', 'Stone Eagle', 'Accounting') AND [SWBC.AS400Task]!= 'None' AND [SWBC.AS400Task]!= '' AND [System.Tags] Not Contains 'ETL'`;
    // const wiqlLendOne = `SELECT [System.Id] FROM WorkItems ... ${lendOneDateFilter} ...`; // Add LendOne query if needed
    console.log(wiqlAS400)
    try {
        // Fetch all data in parallel where possible
        const [
            dmIds, docIds, releaseFigWebDevIds, releaseLpoIds, releaseSuretrackIds, as400Ids //, lendOneIds
        ] = await Promise.all([
            queryWorkItems(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, wiqlDM),
            queryWorkItems(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, wiqlDoc),
            queryWorkItems(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, wiqlReleaseFigWebDev),
            queryWorkItems(ADO_CONFIG.lpoOrgUrl, ADO_CONFIG.pat, wiqlReleaseLpo),
            queryWorkItems(ADO_CONFIG.suretrackOrgUrl, ADO_CONFIG.pat, wiqlReleaseSuretrack),
            queryWorkItems(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, wiqlAS400),
            // queryWorkItems(ADO_CONFIG.lendOneOrgUrl, ADO_CONFIG.lendOnePat, wiqlLendOne), // Add LendOne if needed
        ]);

        const [
            dmItems, docItems, releaseFigWebDevItems, releaseLpoItems, releaseSuretrackItems, as400Items //, lendOneItems
        ] = await Promise.all([
            getWorkItemBatch(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, dmIds),
            getWorkItemBatch(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, docIds),
            getWorkItemBatch(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, releaseFigWebDevIds),
            getWorkItemBatch(ADO_CONFIG.lpoOrgUrl, ADO_CONFIG.pat, releaseLpoIds),
            getWorkItemBatch(ADO_CONFIG.suretrackOrgUrl, ADO_CONFIG.pat, releaseSuretrackIds),
            getWorkItemBatch(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, as400Ids),
             // getWorkItemBatch(ADO_CONFIG.lendOneOrgUrl, ADO_CONFIG.lendOnePat, lendOneIds), // Add LendOne if needed
        ]);

        // Process individual batches
        const processedDM = await processApiResponse(dmItems, 'DM', ADO_CONFIG);
        const processedDoc = await processApiResponse(docItems, 'Doc', ADO_CONFIG);
        const processedRelFig = await processApiResponse(releaseFigWebDevItems, 'Release', ADO_CONFIG);
        const processedRelLpo = await processApiResponse(releaseLpoItems, 'Release',ADO_CONFIG, true ); // Mark as LPO
        const processedRelSure = await processApiResponse(releaseSuretrackItems, 'Release', ADO_CONFIG, false, true); // Mark as Suretrack
        const processedAS400 = await processApiResponse(as400Items, 'AS400', ADO_CONFIG);
        // const processedLendOne = processApiResponse(lendOneItems, 'Release', false, false, true); // Mark as LendOne

        // Combine all processed items
        // **TODO**: Ensure the combination logic matches the PS script ($BatchDataManagement + $BatchDocumentum + $BatchReleasesGrouped + $BatchAS400Grouped)
        // The PS script groups *before* combining for the final $myData object.
        // This needs careful replication. The grouping below is a simplified example.
        const allProcessedItems = [
            ...processedDM,
            ...processedDoc,
            ...processedRelFig,
            ...processedRelLpo,
            ...processedRelSure,
            ...processedAS400,
            // ...processedLendOne
            ];

        // Group and sort the combined data
        const groupedData = groupAndSortDataAgenda(allProcessedItems);

        const groupedDataTechNotify = groupAndSortDataTechNotify(allProcessedItems, dateFromInput);

        const groupedDataCoord = groupAndSortDataCoord(allProcessedItems, dateFromInput);


        // ** TODO: Re-implement this logic as nullable 
        let onCall = WhoIsOnCall(0, rotation, dateFromInput);
        let onCallNextWeek = WhoIsOnCall(1, rotation, dateFromInput);
        if (onCallOverride !== "default") {onCall = onCallOverride}
        if (onCallNextWeekOverride !== "default") {onCallNextWeek = onCallNextWeekOverride}

        // const SIGNATURE_HTML = signature || ""; // Assuming this is the signature HTML
        // const cssDoNotInline = ""
        let AgendaSubject
        const TechNotifySubject: string = `Deployments Starting - ${new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Chicago' })}`
        const CoordinationSubject: string = `Deployment Coordination - ${new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Chicago' })}`
        if (dateFrom == dateTo) {AgendaSubject = `Deployment Agenda - ${new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Chicago' })}`}
        else {AgendaSubject = `Deployment Agenda - ${new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Chicago' })} to ${new Date(dateTo + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'America/Chicago' })}`}

        // Prepare final data structure for rendering
        const finalAgendaData: AgendaData = {
                Subject: AgendaSubject,
                OnCall: onCall,
                OnCallNextWeek: onCallNextWeek,
                DepartmentReleaseSet: groupedData,
                Signature: SIGNATURE_HTML, // Added signature
                stylesDoNotInline: cssDoNotInline // Added empty styles placeholder
            };
        const finalTechNotifyData: TechNotifyData = {
            Subject: TechNotifySubject,
            Releases: groupedDataTechNotify,
            OnCall: onCall,
            Signature: SIGNATURE_HTML, // Added signature
            stylesDoNotInline: cssDoNotInline // Added empty styles placeholder
        };
        const finalCoordData: TechNotifyData = {
            Subject: CoordinationSubject,
            Releases: groupedDataCoord,
            OnCall: onCall,
            Signature: SIGNATURE_HTML, // Added signature
            stylesDoNotInline: cssDoNotInline // Added empty styles placeholder
        }
            const agenda = await renderAgenda(finalAgendaData, agendaTemplate ); // Render the agenda with the final data
            const techNotify = await renderTechNotify(finalTechNotifyData, techNotifyTemplate );
            const coord = await renderTechNotify(finalCoordData, coordTemplate)

            return [agenda, techNotify, coord]

    } catch (error) {
        console.error("Failed to load or render agenda:", error);
        throw "NPM SRA PackageFailed to load or render agenda."
    }
}

async function fetchAdo<T>(ADO_CONFIG: AdoConfig, url: string, method: string = 'GET', body?: any): Promise<T> {
  console.log(`Workspaceing ADO: ${method} ${url}`);
  try {
      const response = await fetch(url, {
          method: method,
          headers: getAuthHeader(ADO_CONFIG.pat), // Assumes getAuthHeader function exists
          body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
          const errorText = await response.text();
          console.error(`ADO API Error (${response.status}) for ${url}: ${errorText}`);
          throw new Error(`ADO API Error (${response.status}): ${errorText.substring(0, 500)}`);
      }
      if (response.status === 204) { // No content
          return null as T;
      }
      return await response.json() as T;
  } catch (error) {
      console.error(`Workspace error for ${url}:`, error);
      throw error; // Re-throw
  }
}

/**
* Extracts organization, project, pipeline type, and ID from a URL.
* Mimics Python extract_details_from_url.
*/
async function extractDetailsFromUrl(url: string): Promise<ReleaseDetails> | null {
  const releasePattern = new RegExp(/https:\/\/dev\.azure\.com\/(?<organization>[^\/]+)\/(?<project>[^\/]+)\/.+releaseId=(?<id>\d+)/, "i");
  const buildPattern = new RegExp(/https:\/\/dev\.azure\.com\/(?<organization>[^/]+)\/(?<project>[^/]+)\/_build\/results\?.*?buildId=(?<id>\d+)/g,"i");

  const releaseMatch  = url.match(releasePattern);
  if (releaseMatch !== null) {
    console.log("Release match found for URL:", url);
    console.log(releaseMatch);
      return {
          organization: releaseMatch.groups.organization,
          project: releaseMatch.groups.project,
          pipelineType: 'release',
          id: releaseMatch.groups.id,
      };
  }

  const buildMatch = url.match(buildPattern);
  if (buildMatch) {
    console.log("Build match found for URL:", url);
      return {
          organization: buildMatch.groups.organization,
          project: buildMatch.groups.project,
          pipelineType: 'build',
          id: buildMatch.groups.id,
      };
  }

  console.error("URL does not match expected patterns:", url);
  return null;
}

// --- Core Logic Functions (Mimicking Python script) ---

/**
 * Fetches details for a specific work item.
 * Mimics Python get_work_item_details.
 */
async function getWorkItemDetails(ADO_CONFIG: AdoConfig, workItemApiUrl: string): Promise<WorkItem> {
  // Add api-version if not present
  const url = workItemApiUrl.includes('api-version=') ? workItemApiUrl : `${workItemApiUrl}?api-version=7.0`;
  return fetchAdo<WorkItem>(ADO_CONFIG, url);
}

/**
* Fetches a work item with expanded relations.
* Mimics Python get_related_work_items.
*/
async function getWorkItemWithRelations(ADO_CONFIG: AdoConfig, organization: string, project: string, workItemId: number | string): Promise<WorkItem> {
  const url = `https://dev.azure.com/${organization}/${project}/_apis/wit/workitems/${workItemId}?$expand=relations&api-version=7.0`;
  return fetchAdo<WorkItem>(ADO_CONFIG,url);
}

/**
* Checks if all stages in a given release have passed or not started.
* Helper for finding previous successful release.
*/
async function allStagesPassed(ADO_CONFIG: AdoConfig, organization: string, project: string, releaseId: number): Promise<boolean> {
  try {
      const url = `https://vsrm.dev.azure.com/${organization}/${project}/_apis/release/releases/${releaseId}?api-version=6.0`;
      const releaseDetails = await fetchAdo<AdoRelease>(ADO_CONFIG, url);
      if (!releaseDetails || !releaseDetails.environments) return false; // Handle unexpected response

      return releaseDetails.environments.every(env =>
          ['succeeded', 'partiallySucceeded', 'notStarted', 'canceled'].includes(env.status) // Adjust statuses as needed
      );
  } catch (error) {
      console.warn(`Could not check status for release ${releaseId}, assuming not passed:`, error);
      return false; // Assume not passed if there's an error fetching details
  }
}

/**
* Fetches work items linked to a specific build or release.
* Mimics Python get_linked_work_items.
*/
async function getLinkedWorkItems(details: ReleaseItem, ADO_CONFIG: AdoConfig): Promise<AdoWorkItemRef[]> {
  const organization = details.Organization;
  const project = details.TeamProject;
  const pipelineType = details.PipelineType;
  const id  = details.BuildId;
  let workItems: AdoWorkItemRef[] = [];
  const seenIds = new Set<number>();

  if (pipelineType === "build") {
      const url = `https://dev.azure.com/${organization}/${project}/_apis/build/builds/${id}/workitems?api-version=7.0`;
      const response = await fetchAdo<{ value: AdoWorkItemRef[] }>(ADO_CONFIG, url);
      workItems = response?.value ?? [];

  } else if (pipelineType === "release") {
      // Get target release details
      const targetReleaseUrl = `https://vsrm.dev.azure.com/${organization}/${project}/_apis/release/releases/${id}?api-version=7.0`;
      const targetRelease = await fetchAdo<AdoRelease>(ADO_CONFIG, targetReleaseUrl);
      if (!targetRelease?.releaseDefinition?.id) {
          throw new Error(`Could not get release definition ID for release ${id}`);
      }
      const definitionId = targetRelease.releaseDefinition.id;

      // --- Find Previous Successful Release ---
      const releasesUrl = `https://vsrm.dev.azure.com/${organization}/${project}/_apis/release/releases?definitionId=${definitionId}&api-version=7.0&$top=100`; // Limit results
      const releasesResponse = await fetchAdo<{ value: AdoRelease[] }>(ADO_CONFIG, releasesUrl);
      let previousReleaseId: number | null = null;
      if (releasesResponse?.value) {
          const sortedReleases = releasesResponse.value
              .filter(r => r.id < targetRelease.id) // Only consider previous releases
              .sort((a, b) => b.id - a.id); // Sort descending by ID (most recent first)

          for (const release of sortedReleases) {
              if (await allStagesPassed(ADO_CONFIG, organization, project, release.id)) {
                  previousReleaseId = release.id;
                  break;
              }
          }
      }
      console.log(`Previous successful release ID found for ${id}: ${previousReleaseId}`);

      // --- Get Changes and Commit Work Items ---
      if (previousReleaseId !== null && targetRelease.artifacts) {
          // Find primary Git artifact to get repo name
          const primaryGitArtifact = targetRelease.artifacts[0] ?? null;
          const repoName = primaryGitArtifact.definitionReference.repository.name ?? null; // Adjust based on actual structure

          if (!repoName) {
               console.warn(`Could not find primary Git repository name for release ${id}. Cannot fetch commit work items.`);
               return []; // Or handle differently
          }

          // Get commits between releases (Note: This endpoint seems deprecated/changed, might need alternative)
          // The python uses /release/releases/{target_release_id}/changes?baseReleaseId={previous_release_id}
          // Let's try fetching commits directly associated with the build artifacts if possible,
          // or fall back to querying commits in the repo if the 'changes' endpoint fails.
          // For now, sticking to translating the python approach:
          const changesUri = `https://vsrm.dev.azure.com/${organization}/${project}/_apis/release/releases/${id}/changes?baseReleaseId=${previousReleaseId}&api-version=6.0`; // Using 6.0 as python did

          try {
              const changesResponse = await fetchAdo<{ value: { id: string }[] }>(ADO_CONFIG, changesUri); // 'id' here is commit SHA
              const commits = changesResponse?.value ?? [];

              if (commits.length > 0) {
                  // Fetch work items associated with these commits
                  // This requires the REPOSITORY ID, not just the name. Need another call.
                  const repoUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${encodeURIComponent(repoName)}?api-version=6.0`;
                  const repoDetails = await fetchAdo<AdoGitRepo>(ADO_CONFIG, repoUrl);
                  const repoId = repoDetails.id;

                  if(repoId) {
                      const commitBatchUrl = `https://dev.azure.com/${organization}/${project}/_apis/git/repositories/${repoId}/commitsBatch?api-version=6.0`;
                      const commitShaList = commits.map(c => c.id);
                      // Fetch commit details in batches (API might limit query length)
                      const batchSize = 50;
                      for (let i = 0; i < commitShaList.length; i += batchSize) {
                          const batchShas = commitShaList.slice(i, i + batchSize);
                          const commitDetailsResponse = await fetchAdo<{ value: AdoCommitRef[] }>(ADO_CONFIG, commitBatchUrl, 'POST', { commitIds: batchShas, includeWorkItems: true });
                           if (commitDetailsResponse?.value) {
                               for (const commit of commitDetailsResponse.value) {
                                   commit.workItems?.forEach(wi => {
                                       if (!seenIds.has(wi.id)) {
                                           seenIds.add(wi.id);
                                           workItems.push(wi);
                                       }
                                   });
                               }
                           }
                      }
                  } else {
                      console.warn(`Could not get Repository ID for ${repoName}`);
                  }
              } else {
                   console.log(`No changes found between release ${previousReleaseId} and ${id}.`);
              }

          } catch (changesError) {
               console.warn(`Error fetching changes between releases ${previousReleaseId} and ${id}. This endpoint might be unavailable or deprecated. Error: ${changesError}`);
               // Fallback: Maybe try getting work items from build artifact? (More complex)
          }

      } else {
          console.log(`No previous successful release found for release ${id}. Cannot determine delta work items.`);
           // Fallback: Fetch work items associated directly with the release environment or artifacts? (Complex)
      }
  } else {
      throw new Error(`Invalid pipeline type: ${pipelineType}`);
  }

  // Remove duplicates just in case
  return Array.from(new Map(workItems.map(item => [item.id, item])).values());
}


/**
* Checks if a work item has associated testing artifacts (Test Case, Task) in the correct state,
* or if it's tagged with 'no testing'.
* Mimics Python check_test_cases_or_no_testing.
*/
async function checkTestingCompliance(
  workItemDetails: WorkItem,
  relatedItemsDetails: WorkItem, // Should already contain relations
  organization: string,
  project: string,
  ADO_CONFIG: AdoConfig
): Promise<{ compliant: boolean; wrongStateTestCases: WorkItem[] }> {

  const fields = workItemDetails.fields;
  const tags = fields["System.Tags"] || "";
  const itemType = fields["System.WorkItemType"];
  let tested = false;
  const wrongStateTestCases: WorkItem[] = []; // Collect non-compliant test cases/tasks

  // 1. Check for "no testing" tag (case-insensitive)
  if (/\bno.*?testing\b/i.test(tags)) {
      console.log(`WI ${workItemDetails.id}: Found 'no testing' tag.`);
      return { compliant: true, wrongStateTestCases };
  }

  // 2. Releases don't need explicit testing linked? (Based on Python code)
  if (itemType === "Release") {
       console.log(`WI ${workItemDetails.id}: Is a Release WIT, skipping testing check.`);
       return { compliant: true, wrongStateTestCases };
  }


  // 3. Check linked items
  if (!relatedItemsDetails.relations) {
      console.log(`WI ${workItemDetails.id}: No relations found to check for testing.`);
      return { compliant: false, wrongStateTestCases }; // Not compliant if no relations and no "no testing" tag (unless Release WIT)
  }

  const validTestedByStates = ["Done", "Completed", "Ready", "Shipped", "Closed", "Ready for Production", "Removed", "Ready For Deployment"];
  const relationTypesToCheck = [
      "System.LinkTypes.Hierarchy-Forward", // Child Test Cases/Tasks?
      "Microsoft.VSTS.Common.TestedBy-Forward", // Explicitly Tested By
      "System.LinkTypes.Related", // Might contain test cases/tasks?
      "Microsoft.VSTS.Common.TestedBy-Reverse" // Explicitly Tests
      // Add others? "System.LinkTypes.Hierarchy-Reverse" (Parent)?
  ];

  // let relatedCheckPromises: Promise<void>[] = []; // Array to hold promises for concurrent execution
  // if (relatedItemsDetails.relations) {
    // Use Promise.all to fetch details of relevant related items concurrently

  if (!relatedItemsDetails.relations || !Array.isArray(relatedItemsDetails.relations) || relatedItemsDetails.relations.length === 0) {
    console.warn("No valid relations found for this work item.");
    return { compliant: false, wrongStateTestCases: [] };
  }
  
  const validRelations = relatedItemsDetails.relations.filter(
      relation => relation && relation.rel && relationTypesToCheck.includes(relation.rel)
  );


  const relatedCheckPromises = validRelations.map(async (relation) => {
    try {
      const relatedDetails = await getWorkItemDetails(ADO_CONFIG, relation.url); // Fetch full details
      const relatedFields = relatedDetails.fields;
      const relatedItemType = relatedFields["System.WorkItemType"];
      const relatedStatus = relatedFields["System.State"];
      const relatedActivity = relatedFields["Microsoft.VSTS.Common.Activity"];
      const relatedWebUrl = relatedDetails._links?.html?.href ?? "Unknown URL";
      const relatedId = relatedDetails.id;

      if (relatedItemType === "Test Case") {
          if (validTestedByStates.includes(relatedStatus)) {
              console.log(`WI ${workItemDetails.id}: Related Test Case ${relatedId} is compliant (State: ${relatedStatus}).`);
              tested = true; // Mark as tested if *any* valid TC is found
          } else {
              console.log(`WI ${workItemDetails.id}: Related Test Case ${relatedId} is in wrong state: ${relatedStatus}.`);
              wrongStateTestCases.push(workItemDetails);
              // Python script still considered it 'tested = True' here, meaning the presence of a TC matters most?
              // Let's stick to the Python logic for now: if *any* TC is linked, report compliance = true, but collect wrong states.
              tested = true;
          }
      } else if (relatedItemType === "Task" && relatedActivity === "Testing") {
          if (validTestedByStates.includes(relatedStatus)) {
              console.log(`WI ${workItemDetails.id}: Related Testing Task ${relatedId} is compliant (State: ${relatedStatus}).`);
              tested = true;
          } else {
              console.log(`WI ${workItemDetails.id}: Related Testing Task ${relatedId} is in wrong state: ${relatedStatus}.`);
              wrongStateTestCases.push(workItemDetails);
              // Again, Python logic implies presence is enough.
                tested = true;
          }
      }
    } catch (error) {
                console.error(`Failed to check related item ${relation.url} for WI ${workItemDetails.id}:`, error);
                // Decide how to handle errors - fail compliance? Skip?
    }
  });
      // }
    

  await Promise.all(relatedCheckPromises); // Wait for all checks to complete

  console.log(`WI ${workItemDetails.id}: Final tested status: ${tested}`);
  return { compliant: tested, wrongStateTestCases: [...new Set(wrongStateTestCases)] }; // Return unique wrong state items
}

/**
* Analyzes linked work items for a release, checking status and testing compliance.
* Mimics Python display_work_items but returns structured violation data.
*/
async function analyzeWorkItems(
  linkedWorkItems: AdoWorkItemRef[],
  organization: string,
  project: string,
  ADO_CONFIG: AdoConfig
): Promise<{ nonCompliantNoTesting: WorkItem[]; wrongStateWorkItems: WorkItem[]; wrongStateTestCases: WorkItem[] }> {

  const nonCompliantWorkItems: WorkItem[] = []; // Missing testing OR 'no-testing' tag
  const wrongStateWorkItems: WorkItem[] = []; // Not in a 'Done' state
  let allWrongStateTestCases: WorkItem[] = []; // Test cases/tasks in wrong state

  const validWorkItemEndStates = ["Done", "Completed", "Ready", "Shipped", "Closed", "Ready for Production", "Removed", "Ready For Deployment"];

  for (const itemRef of linkedWorkItems) {
      try {
          const workItemId = itemRef.id;
          console.log(`Analyzing primary linked WI: ${workItemId}`);
          // Fetch item with relations in one call
          const itemWithRelations = await getWorkItemWithRelations(ADO_CONFIG, organization, project, workItemId);
          // const fields = itemWithRelations.fields;
          // const itemType = fields["System.WorkItemType"];
          const status = itemWithRelations.fields["System.State"];
          // const webUrl = itemWithRelations._links?.html?.href ?? "Unknown URL";

          // 1. Check Work Item State Compliance
          if (!validWorkItemEndStates.includes(status)) {
              console.log(`WI ${workItemId}: State is non-compliant: ${status}`);
              wrongStateWorkItems.push(itemWithRelations);
          }

          // 2. Check Testing Compliance (only for the primary linked item, not its children/related)
          const testingCheck = await checkTestingCompliance(itemWithRelations, itemWithRelations, organization, project, ADO_CONFIG);
          if (!testingCheck.compliant) {
               console.log(`WI ${workItemId}: Testing compliance failed.`);
               nonCompliantWorkItems.push(itemWithRelations);
          }
          // Collect any test cases/tasks found to be in the wrong state during the check
          allWrongStateTestCases = allWrongStateTestCases.concat(testingCheck.wrongStateTestCases);

          // Note: The Python script also iterated through *related* items and checked *their* state too (prefix="RELATED-").
          // This seems redundant if the goal is to check the state of items *directly linked* to the release/build.
          // Let's focus on the state/compliance of the *directly linked* items and their *associated* testing items for clarity.

      } catch (error) {
          console.error(`Failed to analyze work item ${itemRef.id}:`, error);
          // Decide how to report this error - maybe add to a separate error list?
          wrongStateWorkItems.push(`Error-${itemRef.id} - Failed to fetch/analyze - ${error instanceof Error ? error.message : error}`);
      }
  }

  return {
      nonCompliantNoTesting: [...new Set(nonCompliantWorkItems)],
      wrongStateWorkItems: [...new Set(wrongStateWorkItems)],
      wrongStateTestCases: [...new Set(allWrongStateTestCases)],
  };
}

/**
* Formats the analysis results into an HTML string suitable for display.
* Replaces the email generation logic.
*/
function formatReportHtml(processedReleases: { details: ParsedReleaseInfo; violations: { nonCompliantNoTesting: WorkItem[]; wrongStateWorkItems: WorkItem[]; wrongStateTestCases: WorkItem[] } }[]): string {
  let html = `<p>We've reviewed the work items associated with the following releases. Please review the details below and take any necessary actions.</p>`;
  html += "<hr>";

  if (processedReleases.length === 0) {
      html += "<p>No valid release data found in the input.</p>";
      return html;
  }

  processedReleases.forEach(releaseInfo => {
      const { details, violations } = releaseInfo;

      html += `<div>`;
      html += `<p><strong>Release Name:</strong> <a href="${details.release}" target="_blank">${details.TeamProject} - ${details.ReleaseName || 'N/A'}</a></p>`;
      html += `<p><strong>Release Work Item:</strong> <a href="${details.WorkItemUrl}">${details.Type} ${details.WID} ${details.Description || 'N/A'}</a></p>`;
      // html += `<p><strong>Release URL:</strong> <a href="${details.Release}" target="_blank">${details.Release}</a></p>`;
      html += `<p><strong>Deploy Date:</strong> ${details.DDHumanFormat || 'N/A'}</p>`;
      html += `<p><strong>Dev Manager:</strong> ${details.DevMan || 'N/A'} (${details.DevManEmail || 'N/A'})</p>`;
      html += `<p><strong>Developer:</strong> ${details.Developer || 'N/A'}</p>`;

      const hasViolations = violations.nonCompliantNoTesting.length > 0 ||
                            violations.wrongStateWorkItems.length > 0 ||
                            violations.wrongStateTestCases.length > 0;

      if (!hasViolations) {
          html += `<p><strong style="color:green">All linked work items appear to be in a proper state and meet testing compliance requirements for this release.</strong></p>`;
      } else {
          if (violations.nonCompliantNoTesting.length > 0) {
              html += `<p><strong style="color:red">The following work items do not have required test cases or test tasks attached/passed and are not tagged 'no testing'.</strong> To resolve this, link your completed Test Case work item(s), link your Test Task (make sure the "Activity" field = "Testing"), or add a "No Testing" tag to the work item.</p>`;
              html += `<ul class="violation-list">`;
              violations.nonCompliantNoTesting.forEach(item => html += `<li><a href="${item._links.html?.href}">${item.fields['System.WorkItemType']} ${item.id}</a> - "${item.fields['System.Title']}" assigned to <a href="https://teams.microsoft.com/l/chat/0/0?users=${item.fields['System.AssignedTo']?.uniqueName}">${item.fields['System.AssignedTo']?.displayName}</a></li>`);
              html += `</ul><br>`;
          }
          if (violations.wrongStateTestCases.length > 0) {
              html += `<p><strong style="color:red">The following associated Test Cases/Testing Tasks are not in a state besides 'Design'.</strong> All test cases for lower environments should be completed, tests for Production should be in a 'ready' state.</p>`;
              html += `<ul class="violation-list">`;
              violations.wrongStateTestCases.forEach(item => html += `<li><a href="${item._links.html?.href}">${item.fields['System.WorkItemType']} ${item.id}</a> - "${item.fields['System.Title']}" assigned to <a href="https://teams.microsoft.com/l/chat/0/0?users=${item.fields['System.AssignedTo']?.uniqueName}">${item.fields['System.AssignedTo']?.displayName}</a> is in state ${item.fields['System.State']}</li>`);
              // violations.wrongStateTestCases.forEach(item => html += `<li><a href="${item._links.html}">${item.fields['System.WorkItemType']} ${item.id}</a> - "${item.fields['System.Title']}" assigned to <a href="https://teams.microsoft.com/l/chat/0/0?users=${item.fields['System.AssignedTo']?.uniqueName}&topicName=${encodeURIComponent(details.ReleaseName)}&message=Could%20you%20please%20check%20whether%20<a%20href="${item._links.html}">${item.fields['System.WorkItemType']}%20${item.id}</a>%20is%20in%20the%20right%20state?">${item.fields['System.AssignedTo']?.displayName} is in state ${item.fields['System.State']}</li>`);
              html += `</ul><br>`;
          }
          if (violations.wrongStateWorkItems.length > 0) {
              html += `<p><strong style="color:red">The following linked work items are not in a Ready for Deployment (or equivalent) state:</strong></p>`;
              html += `<ul class="violation-list">`;
              violations.wrongStateWorkItems.forEach(item => html += `<li><a href="${item._links.html?.href}">${item.fields['System.WorkItemType']} ${item.id}</a> - "${item.fields['System.Title']}" assigned to <a href="https://teams.microsoft.com/l/chat/0/0?users=${item.fields['System.AssignedTo']?.uniqueName}">${item.fields['System.AssignedTo']?.displayName}</a> is in state ${item.fields['System.State']}</li>`);
              // violations.wrongStateWorkItems.forEach(item => html += `<li><a href="${item._links.html}">${item.fields['System.WorkItemType']} ${item.id}</a> - "${item.fields['System.Title']}" assigned to <a href="https://teams.microsoft.com/l/chat/0/0?users=${item.fields['System.AssignedTo']?.uniqueName}&topicName=${encodeURIComponent(details.ReleaseName)}&message=Could%20you%20please%20check%20whether%20<a%20href="${item._links.html}">${encodeURIComponent(item.fields['System.WorkItemType'])}%20${item.id}</a>%20is%20ready%20to%20be%20completed?">${item.fields['System.AssignedTo']?.displayName} is in state ${item.fields['System.State']}</li>`);
              // violations.wrongStateWorkItems.forEach(item => html += `<li><a href="${item._links.html}">${item.fields['System.WorkItemType']} ${item.id}</a> - ${item.fields['System.Title']} assigned to ${item.fields['System.AssignedTo']?.displayName} is in state ${item.fields['System.State']}</li>`);
              html += `</ul><br>`;
          }
      }
      html += `</div><hr>`; // Separator for next release
  });

  return html;
}


// /**
// * Parses the input text (content of BatchReleases.txt) to extract release details.
// * Mimics Python regex parsing in process_urls.
// */
// function parseReleaseDataInput(inputText: string): ParsedReleaseInfo[] {
//   const releases: ParsedReleaseInfo[] = [];
//   // Regex adapted from Python script
//   const pattern = `/DDHumanFormat\s+:\s+([^\n]*)\nDevMan\s+:\s+([^\n]*)\nDevManEmail\s+:\s+([^\n]*)\nDeveloper\s+:\s+([^\n]*)\n(?:Tester\s+:\s+[^\n]*\n)?HasRelease\s+:\s+True\nRelease\s+:\s+(https://[^\n]*)\nReleaseId\s+:\s+([^\n]*)\nReleaseName\s+:\s+([^\n]*)\n(?:WID\s+:\s+[^\n]*\n)?Description\s+:\s+([^\n]*)/gm`;

//   let match;
//   while ((match = pattern.exec(inputText)) !== null) {
//       releases.push({
//           DeployDate: match[1]?.trim() || 'N/A',
//           DevMan: match[2]?.trim() || 'N/A',
//           DevManEmail: match[3]?.trim() || 'N/A',
//           Developer: match[4]?.trim() || 'N/A',
//           ReleaseURL: match[5]?.trim() || 'N/A',
//           ReleaseID: match[6]?.trim() || 'N/A',
//           ReleaseName: match[7]?.trim() || 'N/A',
//           Description: match[8]?.trim() || 'N/A',
//       });
//   }
//   console.log(`Parsed ${releases.length} release sections from input.`);
//   return releases;
// }


// --- Main Execution ---
export async function generateReport(ADO_CONFIG: AdoConfig, dateFromInput): Promise<string> {
  const results = [];
  const dateFrom = dateFromInput || new Date(new Date().toLocaleDateString('en-US', { timeZone: 'America/Chicago' })).toISOString().split('T')[0]; // Default to today if empty
  const currentTime = new Date().toLocaleTimeString('en-US', { hour12: false }); // Format as HH:mm:ss
  console.log(`Fetching data for RWIS date: ${dateFrom} at ${currentTime}`); // Debugging output

  // ** CRITICAL STEP ** : Construct WIQL queries based on PS script and selected date
  // Need to replicate the logic for $OneDate vs $DateRange based on whether ReleaseDateTo is set.
  // This example uses $OneDate logic.
  const dateFilter = `AND ([Microsoft.VSTS.Common.DD] = '${dateFrom}' OR [Microsoft.VSTS.Common.ImpDate] = '${dateFrom}')`; // Basic date filter
  // const dateFilter = `AND (([Microsoft.VSTS.Common.DD] >= '${dateFrom}' AND [Microsoft.VSTS.Common.DD] <= '${dateTo}') OR ([Microsoft.VSTS.Common.ImpDate] >=  '${dateFrom}' AND [Microsoft.VSTS.Common.ImpDate] <= '${dateTo}'))`; // Date range filter
  // const lendOneDateFilter = `AND [Custom.DeploymentDate] = '${dateFrom}'`; // Example for LendOne i.e. new orgs that can't use the same naming convention

  // Construct WIQL queries - **These must match the PS script exactly**
  // Example WIQL (replace {{Dates}} correctly)
  const wiqlReleaseFigWebDev = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType]='Release' ${dateFilter} AND [SWBC.Department]!= 'Swivel Engineering'`; // Default Release Query
  const wiqlReleaseLpo = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType]='Release' ${dateFilter}`; // LPO Release Query
  const wiqlReleaseSuretrack = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType]='Release' ${dateFilter}`; // Suretrack Release Query
  const wiqlAS400 = `SELECT [System.Id] FROM WorkItems WHERE [System.WorkItemType] in ('Product Backlog Item', 'Bug') ${dateFilter} AND [SWBC.ApplicationType] in ('Auto', 'Gap', 'Mortgage', 'Stone Eagle', 'Accounting') AND [SWBC.AS400Task]!= 'None' AND [SWBC.AS400Task]!= '' AND [System.Tags] Not Contains 'ETL'`;
  // const wiqlLendOne = `SELECT [System.Id] FROM WorkItems ... ${lendOneDateFilter} ...`; // Add LendOne query if needed

  let parsedReleases: Array<ReleaseItem>

  try {
      // Fetch all data in parallel where possible
      const [
          releaseFigWebDevIds, releaseLpoIds, releaseSuretrackIds, as400Ids //, lendOneIds
      ] = await Promise.all([
          queryWorkItems(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, wiqlReleaseFigWebDev),
          queryWorkItems(ADO_CONFIG.lpoOrgUrl, ADO_CONFIG.pat, wiqlReleaseLpo),
          queryWorkItems(ADO_CONFIG.suretrackOrgUrl, ADO_CONFIG.pat, wiqlReleaseSuretrack),
          queryWorkItems(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, wiqlAS400),
          // queryWorkItems(ADO_CONFIG.lendOneOrgUrl, ADO_CONFIG.lendOnePat, wiqlLendOne), // Add LendOne if needed
      ]);

      const [
          releaseFigWebDevItems, releaseLpoItems, releaseSuretrackItems, as400Items //, lendOneItems
      ] = await Promise.all([
          getWorkItemBatch(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, releaseFigWebDevIds),
          getWorkItemBatch(ADO_CONFIG.lpoOrgUrl, ADO_CONFIG.pat, releaseLpoIds),
          getWorkItemBatch(ADO_CONFIG.suretrackOrgUrl, ADO_CONFIG.pat, releaseSuretrackIds),
          getWorkItemBatch(ADO_CONFIG.orgUrl, ADO_CONFIG.pat, as400Ids),
           // getWorkItemBatch(ADO_CONFIG.lendOneOrgUrl, ADO_CONFIG.lendOnePat, lendOneIds), // Add LendOne if needed
      ]);

      // Process individual batches
      const processedRelFig = await processApiResponse(releaseFigWebDevItems, 'Release', ADO_CONFIG);
      const processedRelLpo = await processApiResponse(releaseLpoItems, 'Release',ADO_CONFIG, true ); // Mark as LPO
      const processedRelSure = await processApiResponse(releaseSuretrackItems, 'Release', ADO_CONFIG, false, true); // Mark as Suretrack
      const processedAS400 = await processApiResponse(as400Items, 'AS400', ADO_CONFIG);
      // const processedLendOne = processApiResponse(lendOneItems, 'Release', false, false, true); // Mark as LendOne

      // Combine all processed items
      // **TODO**: Ensure the combination logic matches the PS script ($BatchDataManagement + $BatchDocumentum + $BatchReleasesGrouped + $BatchAS400Grouped)
      // The PS script groups *before* combining for the final $myData object.
      // This needs careful replication. The grouping below is a simplified example.
      const allProcessedItems = [
          ...processedRelFig,
          ...processedRelLpo,
          ...processedRelSure,
          ...processedAS400,
          // ...processedLendOne
          ];

      // Group and sort the combined data
          console.log(allProcessedItems);
      parsedReleases = await groupAndSortDataTechNotify(allProcessedItems, dateFromInput);

  
    for (const release of parsedReleases) {
        console.log(`Processing Release: ${release.ReleaseName} (${release.BuildId})`);
        
        const releaseUrl = release.Release;
        console.log(`Release URL: ${releaseUrl}`);
        if (!releaseUrl || !releaseUrl.startsWith('http')) {
            console.warn(`Skipping release "${release.ReleaseName}" due to invalid URL: ${releaseUrl}`);
              results.push({
                  details: release,
                  violations: {
                      nonCompliantNoTesting: [],
                      wrongStateWorkItems: [],
                      wrongStateTestCases: []
                  }
              });
            continue;
        }

        try {
            // const pipelineDetails = await extractDetailsFromUrl(releaseUrl);
            if (!release.PipelineType) {
                throw new Error(`Pipeline type not found in release data for release ${release.Description}.`);
            }

            const linkedWorkItems = await getLinkedWorkItems(release, ADO_CONFIG);
            if (!linkedWorkItems || linkedWorkItems.length === 0) {
                console.log(`No linked work items found for ${release.ReleaseName}.`);
                  results.push({
                      details: release,
                      // Report as success or indicate no items? Assuming success if 0 items.
                      violations: { nonCompliantNoTesting: [], wrongStateWorkItems: [], wrongStateTestCases: [] }
                  });
                continue; // Skip analysis if no items linked
            }

            const violations = await analyzeWorkItems(linkedWorkItems, release.Organization, release.TeamProject, ADO_CONFIG);
            results.push({ details: release, violations });

        } catch (error) {
            console.error(`Failed to process release ${release.ReleaseName}:`, error);
            results.push({
                details: release,
                violations: {
                    nonCompliantNoTesting: [],
                    wrongStateWorkItems: [`Error - Failed to process release: ${error instanceof Error ? error.message : error}`],
                    wrongStateTestCases: []
                }
            });
        }
    }
  } catch (error) {
    console.error("NPM SRA Package Failed to look up and process releases:", error);
    throw "NPM SRA Package Failed to look up and process releases."
}

  // Format the final HTML report
  return formatReportHtml(results);
}


// // --- DOM Setup ---
// const patInputEl = document.getElementById('patInput') as HTMLInputElement;
// const releaseDataInputEl = document.getElementById('releaseDataInput') as HTMLTextAreaElement;
// const generateReportBtnEl = document.getElementById('generateReportBtn');
// const rwisOutputDivEl = document.getElementById('RWIS');

// generateReportBtnEl?.addEventListener('click', async () => {
//   const pat = patInputEl?.value ?? "";
//   const releaseData = releaseDataInputEl?.value ?? "";

//   if (rwisOutputDivEl) {
//       rwisOutputDivEl.innerHTML = "Generating report, please wait...";
//   }

//   try {
//       const reportHtml = await generateReport(pat, releaseData);
//       if (rwisOutputDivEl) {
//           rwisOutputDivEl.innerHTML = reportHtml;
//       }
//   } catch (error) {
//        if (rwisOutputDivEl) {
//           rwisOutputDivEl.innerHTML = `<p class="error">An unexpected error occurred during report generation: ${error instanceof Error ? error.message : error}. Check console for details.</p>`;
//        }
//        console.error("Report generation failed:", error);
//   }
// });